// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';

// TYPES ---------------------------------------------------------------- //

export type ViewMode = 'table' | 'tiles';
export type SortOrder = 'asc' | 'desc';

interface SearchResultsContextType {
  // State variables
  disablePickers: boolean;
  end: string;
  limit: number;
  offset: number;
  order: SortOrder;
  orderBy: string;
  range: string;
  start: string;
  viewMode: ViewMode;

  // State setters
  setDisablePickers: (disable: boolean) => void;
  setEnd: (end: string) => void;
  setLimit: (limit: number) => void;
  setOffset: (offset: number) => void;
  setOrder: (order: SortOrder) => void;
  setOrderBy: (orderBy: string) => void;
  setRange: (range: string) => void;
  setStart: (start: string) => void;
  setViewMode: (mode: ViewMode) => void;

  // Handlers
  onClearFilters: () => void;
  onDateChange: (type: 'start' | 'end') => (date: string) => void;
  onRangeChange: (range: string) => void;
  onViewModeChange: (mode: ViewMode) => void;
}

interface SearchResultsProviderProps {
  children: React.ReactNode;
}

// GETTERS ---------------------------------------------------------------- //

const getViewMode = (): ViewMode => {
  try {
    return (localStorage.getItem(`search-results-view-mode`) as ViewMode) || `table`;
  } catch (err) {
    console.error(`Error getting view mode`, err);
    return `table`;
  }
};

const validRanges = [
  'default',
  'nextweek',
  'thisweek',
  'lastweek',
  'nextmonth',
  'thismonth',
  'lastmonth',
  'nextyear',
  'thisyear',
  'lastyear',
  'custom',
];

const getRange = (returnDefault: boolean = false): string => {
  const defaultRange = 'default';

  try {
    if (returnDefault) return defaultRange;
    return localStorage.getItem('search-results-range') || defaultRange;
  } catch (err) {
    console.error('Error getting range', err);
    return defaultRange;
  }
};

const getStart = (range: string, returnDefault: boolean = false): string => {
  const defaultStart = dayjs().subtract(1, 'year').startOf('day').format();
  const minDate = dayjs().subtract(1, 'year').startOf('day').format();

  if (returnDefault) return defaultStart;

  try {
    const localRange = range || localStorage.getItem('search-results-range');
    const localStart = localStorage.getItem('search-results-start') || defaultStart;
    if (!localRange || !validRanges.includes(localRange)) return defaultStart;

    // Default range (last year + 30 days in the future)
    if (localRange === 'default') return dayjs().subtract(1, 'year').startOf('day').format();

    // Weekly ranges
    if (localRange === 'nextweek') return dayjs().add(1, 'week').startOf('week').format();
    if (localRange === 'thisweek') return dayjs().startOf('week').format();
    if (localRange === 'lastweek') return dayjs().subtract(1, 'week').startOf('week').format();

    // Monthly ranges
    if (localRange === 'nextmonth') return dayjs().add(1, 'month').startOf('month').format();
    if (localRange === 'thismonth') return dayjs().startOf('month').format();
    if (localRange === 'lastmonth') return dayjs().subtract(1, 'month').startOf('month').format();

    // Yearly ranges
    if (localRange === 'nextyear') return dayjs().add(1, 'year').startOf('year').format();
    if (localRange === 'thisyear') return dayjs().startOf('year').format();
    if (localRange === 'lastyear') return dayjs().subtract(1, 'year').startOf('year').format();

    // Custom range
    if (localRange === 'custom') {
      if (localStart < minDate || localStart === 'Invalid Date') return minDate;
      return localStart;
    }

    return defaultStart;
  } catch (err) {
    console.error('Error getting start date', err);
    return defaultStart;
  }
};

const getEnd = (range: string, returnDefault: boolean = false): string => {
  const defaultEnd = dayjs().add(30, 'day').endOf('day').format();
  const maxDate = dayjs().add(1, 'year').endOf('day').format();

  if (returnDefault) return defaultEnd;

  try {
    const localRange = range || localStorage.getItem('search-results-range');
    const localEnd = localStorage.getItem('search-results-end') || defaultEnd;

    if (!localRange || !validRanges.includes(localRange)) return defaultEnd;

    // Default range (last year + 30 days in the future)
    if (localRange === 'default') return dayjs().add(30, 'day').endOf('day').format();

    // Weekly ranges
    if (localRange === 'nextweek') return dayjs().add(1, 'week').endOf('week').format();
    if (localRange === 'thisweek') return dayjs().endOf('week').format();
    if (localRange === 'lastweek') return dayjs().subtract(1, 'week').endOf('week').format();

    // Monthly ranges
    if (localRange === 'nextmonth') return dayjs().add(1, 'month').endOf('month').format();
    if (localRange === 'thismonth') return dayjs().endOf('month').format();
    if (localRange === 'lastmonth') return dayjs().subtract(1, 'month').endOf('month').format();

    // Yearly ranges
    if (localRange === 'nextyear') return dayjs().add(1, 'year').endOf('year').format();
    if (localRange === 'thisyear') return dayjs().endOf('year').format();
    if (localRange === 'lastyear') return dayjs().subtract(1, 'year').endOf('year').format();

    // Custom range
    if (localRange === 'custom') {
      if (localEnd > maxDate || localEnd === 'Invalid Date') return maxDate;
      return localEnd;
    }

    return defaultEnd;
  } catch (err) {
    console.error('Error getting end date', err);
    return defaultEnd;
  }
};

const getDisable = (range: string, returnDefault: boolean = false): boolean => {
  const defaultDisable = true;

  if (returnDefault) return defaultDisable;
  return range !== 'custom';
};

// CONTEXT ---------------------------------------------------------------- //

const SearchResultsContext = React.createContext<SearchResultsContextType | undefined>(undefined);

export const SearchResultsProvider = ({ children }: SearchResultsProviderProps) => {
  // STATE VARIABLES //

  // View Mode State
  const [viewMode, setViewMode] = React.useState<ViewMode>(getViewMode());

  // Filter State
  const [range, setRange] = React.useState<string>(getRange(true));
  const [start, setStart] = React.useState<string>(getStart(range, true));
  const [end, setEnd] = React.useState<string>(getEnd(range, true));
  const [disablePickers, setDisablePickers] = React.useState<boolean>(getDisable(range, true));

  // Pagination State
  const [limit, setLimit] = React.useState<number>(50);
  const [offset, setOffset] = React.useState<number>(0);

  // Sorting State
  const [orderBy, setOrderBy] = React.useState<string>('ready_by');
  const [order, setOrder] = React.useState<SortOrder>('desc');

  // HANDLERS //

  /** Handle changing the view mode */
  const handleViewModeChange = (newViewMode: ViewMode) => {
    localStorage.setItem(`search-results-view-mode`, newViewMode);
    setViewMode(newViewMode);
  };

  /** Handle changing the range */
  const handleRangeChange = (newRange: string) => {
    try {
      localStorage.setItem('search-results-range', newRange);
      const newStart = getStart(newRange);
      const newEnd = getEnd(newRange);

      setRange(newRange);
      setStart(newStart);
      setEnd(newEnd);
      setDisablePickers(getDisable(newRange));
    } catch (error) {
      console.error('Error changing range', error);
    }
  };

  /** Handle changing the date */
  const handleDateChange = (type: 'start' | 'end') => (date: string) => {
    if (!date) return;

    if (type === 'start') {
      const newStart = dayjs(date).startOf('day').format();
      const dateIsValid = new Date(newStart).getTime() > 0;
      if (!dateIsValid) return;

      localStorage.setItem('search-results-start', newStart);
      setStart(newStart);
    } else {
      const newEnd = dayjs(date).endOf('day').format();
      const dateIsValid = new Date(newEnd).getTime() > 0;
      if (!dateIsValid) return;

      localStorage.setItem('search-results-end', newEnd);
      setEnd(newEnd);
    }
  };

  /** Handle clearing the filters */
  const handleClearFilters = () => {
    setOffset(0);
  };

  /** Returning context value */
  const contextValue: SearchResultsContextType = {
    // State variables
    disablePickers,
    end,
    limit,
    offset,
    order,
    orderBy,
    range,
    start,
    viewMode,

    // State setters
    setDisablePickers,
    setEnd,
    setLimit,
    setOffset,
    setOrder,
    setOrderBy,
    setRange,
    setStart,
    setViewMode,

    // Handlers
    onClearFilters: handleClearFilters,
    onDateChange: handleDateChange,
    onRangeChange: handleRangeChange,
    onViewModeChange: handleViewModeChange,
  };

  return <SearchResultsContext.Provider value={contextValue}>{children}</SearchResultsContext.Provider>;
};

// HOOK ---------------------------------------------------------------- //

export const useSearchResults = (): SearchResultsContextType => {
  const context = React.useContext(SearchResultsContext);
  if (context === undefined) {
    throw new Error('useSearchResults must be used within a SearchResultsProvider');
  }
  return context;
};
